import { loadSessionUser } from "@bodemleven/course-kit/react";
import { atLeast } from "@bodemleven/course-kit/schema/core";
import type { QueryClient } from "@tanstack/react-query";
import type { LoaderFunction, Params, ShouldRevalidateFunction } from "react-router";
import type { WorkspaceQuery } from "../lib/workspace-query.ts";
import { workspaceKeys, type WorkspaceKeys } from "./current-user.tsx";

interface PrefetchContext {
  /** Keys of the signed-in user, from the settled session. */
  keys: WorkspaceKeys;
  /** The route's params; a page reads its id with the same `?? ""` default. */
  params: Params;
  /** Analyst or above: the lab work. */
  analyst: boolean;
  /** Admin or above: the Lab settings. */
  admin: boolean;
  /** Fills the cache unless it already holds the query; the page refreshes stale data itself. */
  load: <T>(query: WorkspaceQuery<T>) => Promise<T>;
}

/** The page's main queries, the ones its first render needs. */
export type Prefetch = (context: PrefetchContext) => Promise<unknown>[];

/**
 * Loader of a workspace page: fetches its main queries while the route's `lazy` chunk
 * loads, so the page renders from the cache through its usual `use…` hooks.
 *
 * The user comes from the settled session (never the previous account's keys); signed
 * out, nothing loads and the gate redirects to the login. A failed request does not fail
 * the route: the page's own query shows its error state and retries.
 */
export const prefetchLoader =
  (queryClient: QueryClient, prefetch: Prefetch): LoaderFunction =>
  async ({ params }) => {
    const user = await loadSessionUser();
    if (!user) return null;
    const load = <T>(query: WorkspaceQuery<T>): Promise<T> =>
      queryClient.query({ ...query, staleTime: "static" });
    await Promise.allSettled(
      prefetch({
        keys: workspaceKeys(user.id),
        params,
        analyst: atLeast(user.role, "analyst"),
        admin: atLeast(user.role, "admin"),
        load,
      }),
    );
    return null;
  };

/**
 * Loaders only prefetch, so they run for a new page or record, not when list filters
 * change the search parameters (which would delay every filter keystroke).
 */
export const pathnameChanged: ShouldRevalidateFunction = ({ currentUrl, nextUrl }) =>
  currentUrl.pathname !== nextUrl.pathname;
